/**
 * Change a contact's pipeline stage, with the history underneath.
 *
 * The rule worth stating: **moving backwards requires a reason.** Enforced in
 * the domain layer (SUBSCRIBER → INTERESTED means something went wrong, and
 * whoever reads that history in six months needs to know what). Mirrored here
 * only so the user finds out before submitting — the server is what actually
 * decides, and its refusal surfaces as a normal field error if this check is
 * ever wrong.
 *
 * The history is shown in the same dialog because the question "should this
 * move backwards?" is almost always answered by "what happened last time?".
 */
import { useMutation } from '@apollo/client';
import { StatusBadge } from '@meshhub/design-tokens/ui';
import React, { useEffect, useState } from 'react';
import { COLLABORATION_STATUS_LABELS, formatDateTime } from '../../lib/format.js';
import { CHANGE_STATUS } from '../../lib/queries.js';
import { S } from '../../lib/strings.js';
import { FormDialog, MutationError, SelectField, TextArea } from '../FormKit.web.jsx';

/** The pipeline, in order. Order is what "backwards" means. */
const PIPELINE = ['UNKNOWN', 'INTERESTED', 'CONSULTING', 'SUBSCRIBER'];

const isBackward = (from, to) => PIPELINE.indexOf(to) < PIPELINE.indexOf(from);

export function StatusDialog({ open, onOpenChange, contact, onChanged }) {
  const [toStatus, setToStatus] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState(null);
  const [changeStatus, { loading }] = useMutation(CHANGE_STATUS);

  useEffect(() => {
    if (open) {
      setToStatus('');
      setReason('');
      setError(null);
    }
  }, [open]);

  const backward = toStatus && isBackward(contact.collaborationStatus, toStatus);
  const reasonMissing = backward && !reason.trim();

  async function submit() {
    if (!toStatus) return;
    if (reasonMissing) {
      setError(new Error(S.status.reasonRequiredBackward));
      return;
    }
    setError(null);
    try {
      await changeStatus({
        variables: { contactId: contact.id, toStatus, reason: reason.trim() || null },
      });
      await onChanged?.();
      onOpenChange(false);
    } catch (err) {
      setError(err);
    }
  }

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={S.status.title}
      onSubmit={submit}
      submitLabel={S.status.submit}
      busy={loading}
    >
      <MutationError error={error} />

      <div className="flex items-center gap-3">
        <span className="text-sm text-text-muted">{S.status.current}</span>
        <StatusBadge
          status={contact.collaborationStatus}
          label={COLLABORATION_STATUS_LABELS[contact.collaborationStatus]}
        />
      </div>

      <SelectField
        label={S.status.next}
        value={toStatus}
        onChange={(event) => setToStatus(event.target.value)}
        required
      >
        <option value="">{S.app.none}</option>
        {PIPELINE.filter((status) => status !== contact.collaborationStatus).map((status) => (
          <option key={status} value={status}>
            {COLLABORATION_STATUS_LABELS[status]}
          </option>
        ))}
      </SelectField>

      <TextArea
        label={backward ? S.status.reason : S.status.reasonOptional}
        rows={3}
        value={reason}
        onChange={(event) => setReason(event.target.value)}
        hint={backward ? S.status.reasonRequiredBackward : undefined}
        required={backward}
      />

      {contact.statusHistory.length > 0 && (
        <section>
          <h3 className="mb-2 text-sm font-medium text-text-muted">{S.status.history}</h3>
          <ol className="flex max-h-[30vh] flex-col gap-3 overflow-auto">
            {contact.statusHistory.map((change) => (
              <li key={change.id} className="flex flex-col gap-1 border-s-2 border-border ps-4">
                <div className="flex flex-wrap items-center gap-2">
                  {change.fromStatus && (
                    <>
                      <span className="text-sm text-text-muted">
                        {COLLABORATION_STATUS_LABELS[change.fromStatus]}
                      </span>
                      <span className="text-text-muted" aria-hidden="true">
                        ←
                      </span>
                    </>
                  )}
                  <StatusBadge
                    status={change.toStatus}
                    label={COLLABORATION_STATUS_LABELS[change.toStatus]}
                  />
                  <span className="ms-auto text-xs text-text-muted">
                    {formatDateTime(change.changedAt)}
                  </span>
                </div>
                <p className="text-xs text-text-muted">
                  {S.status.by} {change.changedByName}
                </p>
                {change.reason && <p className="text-sm text-text-muted">{change.reason}</p>}
              </li>
            ))}
          </ol>
        </section>
      )}
    </FormDialog>
  );
}
